/**
 * Table talk: a small WebRTC audio mesh (at most 4 phones → 6 links).
 * Signalling (offers, answers, ICE candidates) travels over the table's WebSocket.
 * For each pair, the lower seat makes the offer, so two phones never offer at once.
 */
import { useEffect, useRef, useState } from 'react';
import type { Seat } from '../game/match';
import type { IceServer, RoomView, RtcSignal } from '../shared/room';
import type { RoomConnection } from './room';
import { toast } from './toast';

interface Peer {
  pc: RTCPeerConnection;
  audio: HTMLAudioElement;
  /** ICE candidates that arrived before the remote description. */
  pending: RTCIceCandidateInit[];
}

let iceServers: Promise<IceServer[]> | null = null;
const loadIceServers = () =>
  (iceServers ??= fetch('/api/turn')
    .then((r) => r.json() as Promise<{ iceServers: IceServer[] }>)
    .then((d) => d.iceServers)
    .catch(() => [{ urls: 'stun:stun.l.google.com:19302' }]));

/** How long a player may vanish from the table (a network blip, a reconnect) before their voice link is cut. */
const LEAVE_GRACE_MS = 8000;

class VoiceMesh {
  private peers = new Map<Seat, Peer>();
  private stream: MediaStream | null = null;
  private leaving = new Map<Seat, number>();
  private ice: IceServer[] = [];

  constructor(
    private me: Seat,
    private signal: (to: Seat, data: RtcSignal) => void,
  ) {}

  async start(): Promise<void> {
    // Fetched once up front so links are created (and registered) synchronously — never two to one player.
    this.ice = await loadIceServers();
    this.stream = await navigator.mediaDevices.getUserMedia({
      // Mono speech with the browser's echo cancelling and noise suppression on.
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
    });
  }

  setMuted(muted: boolean) {
    this.stream?.getAudioTracks().forEach((t) => (t.enabled = !muted));
  }

  /**
   * Connect to everyone else in voice; drop anyone who left. Someone who only
   * blinked out (their socket reconnected) keeps their link — it's cut after a grace period.
   */
  sync(others: Seat[]) {
    for (const [seat, peer] of this.peers) {
      if (others.includes(seat)) {
        clearTimeout(this.leaving.get(seat));
        this.leaving.delete(seat);
      } else if (!this.leaving.has(seat)) {
        this.leaving.set(
          seat,
          window.setTimeout(() => {
            this.leaving.delete(seat);
            if (this.peers.get(seat) === peer) this.drop(seat, peer);
          }, LEAVE_GRACE_MS),
        );
      }
    }
    for (const seat of others) {
      if (!this.peers.has(seat) && this.me < seat) void this.offer(seat);
    }
  }

  /** Some phones only play incoming audio after a tap; call this from any tap. */
  resumeAudio() {
    for (const peer of this.peers.values()) if (peer.audio.paused && peer.audio.srcObject) void peer.audio.play().catch(() => {});
  }

  async onSignal(from: Seat, data: RtcSignal) {
    if ('sdp' in data) {
      if (data.sdp.type === 'offer') {
        // A fresh offer replaces any half-made link (e.g. after the other phone reconnected).
        const old = this.peers.get(from);
        if (old) this.drop(from, old);
        const peer = this.peer(from);
        await peer.pc.setRemoteDescription(data.sdp);
        await this.flush(peer);
        await peer.pc.setLocalDescription(await peer.pc.createAnswer());
        this.signal(from, { sdp: { type: 'answer', sdp: peer.pc.localDescription!.sdp } });
      } else {
        const peer = this.peers.get(from);
        if (peer && peer.pc.signalingState === 'have-local-offer') {
          await peer.pc.setRemoteDescription(data.sdp);
          await this.flush(peer);
        }
      }
    } else {
      const peer = this.peers.get(from);
      if (!peer) return;
      if (peer.pc.remoteDescription) await peer.pc.addIceCandidate(data.candidate).catch(() => {});
      else peer.pending.push(data.candidate);
    }
  }

  stop() {
    this.leaving.forEach((t) => clearTimeout(t));
    this.leaving.clear();
    for (const [seat, peer] of this.peers) this.drop(seat, peer);
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  private async offer(seat: Seat) {
    const peer = this.peer(seat);
    await peer.pc.setLocalDescription(await peer.pc.createOffer());
    this.signal(seat, { sdp: { type: 'offer', sdp: peer.pc.localDescription!.sdp } });
  }

  private peer(seat: Seat): Peer {
    const pc = new RTCPeerConnection({ iceServers: this.ice as RTCIceServer[] });
    const audio = new Audio();
    audio.autoplay = true;
    audio.setAttribute('playsinline', '');
    // Attached to the page: some mobile browsers won't play a detached element's stream.
    audio.hidden = true;
    document.body.appendChild(audio);
    const peer: Peer = { pc, audio, pending: [] };
    this.peers.set(seat, peer);
    this.stream?.getTracks().forEach((t) => pc.addTrack(t, this.stream!));
    pc.onicecandidate = (e) => {
      if (e.candidate) this.signal(seat, { candidate: e.candidate.toJSON() });
    };
    pc.ontrack = (e) => {
      audio.srcObject = e.streams[0] ?? new MediaStream([e.track]);
      void audio.play().catch(() => {});
    };
    let stuck = 0;
    const rebuild = () => {
      if (this.peers.get(seat) !== peer) return;
      this.drop(seat, peer);
      if (this.me < seat) void this.offer(seat);
    };
    pc.onconnectionstatechange = () => {
      clearTimeout(stuck);
      // The offering side rebuilds a link that failed (network switch, Wi-Fi → 4G)…
      if (pc.connectionState === 'failed') rebuild();
      // …or one that dropped and didn't come back by itself.
      else if (pc.connectionState === 'disconnected') stuck = window.setTimeout(rebuild, 6000);
    };
    return peer;
  }

  private async flush(peer: Peer) {
    for (const c of peer.pending.splice(0)) await peer.pc.addIceCandidate(c).catch(() => {});
  }

  private drop(seat: Seat, peer: Peer) {
    peer.pc.close();
    peer.audio.srcObject = null;
    peer.audio.remove();
    this.peers.delete(seat);
  }
}

export const voiceSupportedHere = () => typeof RTCPeerConnection !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

/** Join/leave/mute for the table's voice chat, kept in step with who else is in it. */
export function useTableVoice(conn: RoomConnection, view: RoomView | null) {
  const [joined, setJoined] = useState(false);
  const [muted, setMuted] = useState(false);
  const [starting, setStarting] = useState(false);
  const mesh = useRef<VoiceMesh | null>(null);
  const me = view?.you ?? null;
  // The mic follows only the player's own mute button (never paused for announcements).
  useEffect(() => {
    mesh.current?.setMuted(muted);
  }, [muted, joined]);

  useEffect(() => conn.onRtc((from, data) => void mesh.current?.onSignal(from, data)), [conn]);

  const others = view
    ? view.seatInfo.flatMap((s, seat) => (s.voice && s.online && seat !== me ? [seat as Seat] : []))
    : [];
  const othersKey = others.join(',');
  useEffect(() => {
    if (joined) mesh.current?.sync(others);
    // othersKey captures `others` by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined, othersKey]);

  // After a reconnect the table sees a fresh socket that isn't in voice yet.
  const serverThinksInVoice = me !== null && view?.seatInfo[me]?.voice;
  useEffect(() => {
    if (joined && me !== null && view && !serverThinksInVoice) conn.send({ t: 'voice', on: true, muted });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined, serverThinksInVoice, view !== null]);

  // Leaving the seat (or the page) ends the call.
  useEffect(() => {
    if (me === null && mesh.current) leave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);
  useEffect(() => () => mesh.current?.stop(), []);
  useEffect(() => {
    if (!joined) return;
    const resume = () => mesh.current?.resumeAudio();
    document.addEventListener('pointerdown', resume);
    return () => document.removeEventListener('pointerdown', resume);
  }, [joined]);

  const join = async () => {
    if (me === null || starting) return;
    setStarting(true);
    const m = new VoiceMesh(me, (to, data) => conn.send({ t: 'rtc', to, data }));
    try {
      await m.start();
      mesh.current = m;
      setJoined(true);
      setMuted(false);
      conn.send({ t: 'voice', on: true, muted: false });
    } catch {
      toast('Allow microphone access to talk with the table.', 'error', 6000);
    } finally {
      setStarting(false);
    }
  };

  function leave() {
    mesh.current?.stop();
    mesh.current = null;
    setJoined(false);
    conn.send({ t: 'voice', on: false, muted: false });
  }

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    conn.send({ t: 'voice', on: true, muted: next });
  };

  return { joined, muted, starting, join, leave, toggleMute };
}
